import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { registerAuthTokenGetter } from "../lib/api-client";
import { registerHourlyTokenGetter } from "../lib/hourly-orpc-client";
import { HourlyAuthService } from "../services/HourlyAuthService";
import { refreshSession as cognitoRefreshSession } from "../services/CognitoAuthService";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PersonaType = "manager" | "candidate" | "unauthenticated";

interface HourlyUser {
  userId: string;
  companyId: string;
  name: string;
}

interface HourlyTokens {
  token: string;
}

interface CognitoTokens {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  expiresAt?: number;
}

interface CognitoClaims {
  sub?: string;
  email?: string;
  exp?: number;
}

export interface AuthState {
  persona: PersonaType;
  // Manager auth (hourly JWT)
  hourlyToken: string | null;
  hourlyUser: HourlyUser | null;
  // Candidate auth (Cognito JWT)
  cognitoToken: string | null;
  candidateEmail: string | null;
  candidateId: string | null;
}

interface AuthContextValue extends AuthState {
  loginAsManager: (tokens: HourlyTokens) => void;
  loginAsCandidate: (tokens: CognitoTokens) => void;
  logout: () => void;
  /** Returns the correct token for hiring portal API calls */
  getHiringApiToken: () => string | null;
  /** Returns the hourly JWT (managers only) */
  getHourlyApiToken: () => string | null;
}

// ---------------------------------------------------------------------------
// Storage keys
// ---------------------------------------------------------------------------

const STORAGE_KEY_COGNITO_ID_TOKEN = "ar_cognito_id_token";
const STORAGE_KEY_COGNITO_ACCESS_TOKEN = "ar_cognito_access_token";
const STORAGE_KEY_COGNITO_REFRESH_TOKEN = "ar_cognito_refresh_token";
const STORAGE_KEY_COGNITO_EXPIRES_AT = "ar_cognito_expires_at";
const STORAGE_KEY_HOURLY_TOKEN = "ar_hourly_token";

// ---------------------------------------------------------------------------
// Cognito refresh scheduling (module-level, like HourlyAuthService pattern)
// ---------------------------------------------------------------------------

let _cognitoRefreshTimer: ReturnType<typeof setTimeout> | null = null;
let _cognitoOnRefreshed: ((tokens: { idToken: string; accessToken: string; refreshToken?: string; expiresAt?: number }) => void) | null = null;
let _cognitoOnFailed: (() => void) | null = null;

function clearCognitoRefreshTimer(): void {
  if (_cognitoRefreshTimer !== null) {
    clearTimeout(_cognitoRefreshTimer);
    _cognitoRefreshTimer = null;
  }
}

function scheduleCognitoRefresh(idToken: string): void {
  clearCognitoRefreshTimer();
  try {
    const parts = idToken.split(".");
    if (parts.length !== 3) return;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(base64)) as { exp?: number };
    const exp = payload.exp;
    if (!exp) return;

    // Refresh 5 minutes before expiry
    const msUntilRefresh = (exp - 300) * 1000 - Date.now();
    if (msUntilRefresh <= 0) {
      // Already near expiry — trigger immediately
      void doSilentCognitoRefresh();
      return;
    }

    _cognitoRefreshTimer = setTimeout(() => {
      void doSilentCognitoRefresh();
    }, msUntilRefresh);
  } catch {
    // ignore parse errors
  }
}

async function doSilentCognitoRefresh(): Promise<void> {
  const email = localStorage.getItem("ar_cognito_email");
  const refreshToken = localStorage.getItem(STORAGE_KEY_COGNITO_REFRESH_TOKEN);
  if (!email || !refreshToken) {
    _cognitoOnFailed?.();
    return;
  }

  const result = await cognitoRefreshSession(email, refreshToken);
  if (!result.ok) {
    _cognitoOnFailed?.();
    return;
  }

  // Persist new tokens
  try {
    localStorage.setItem(STORAGE_KEY_COGNITO_ID_TOKEN, result.tokens.idToken);
    localStorage.setItem(STORAGE_KEY_COGNITO_ACCESS_TOKEN, result.tokens.accessToken);
    if (result.tokens.refreshToken) {
      localStorage.setItem(STORAGE_KEY_COGNITO_REFRESH_TOKEN, result.tokens.refreshToken);
    }
    if (result.tokens.expiresAt) {
      localStorage.setItem(STORAGE_KEY_COGNITO_EXPIRES_AT, String(result.tokens.expiresAt));
    }
  } catch {
    // ignore storage errors
  }

  _cognitoOnRefreshed?.(result.tokens);
  scheduleCognitoRefresh(result.tokens.idToken);
}

// ---------------------------------------------------------------------------
// JWT helpers (no library — simple base64 decode)
// ---------------------------------------------------------------------------

function decodeJwtPayload<T>(token: string): T | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1];
    // base64url → base64
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(base64);
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}

interface HourlyJwtPayload {
  userId?: string;
  companyId?: string;
  name?: string;
  sub?: string;
  exp?: number;
}

function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload<{ exp?: number }>(token);
  if (!payload?.exp) return false;
  return Date.now() / 1000 > payload.exp;
}

// ---------------------------------------------------------------------------
// Initial state helpers
// ---------------------------------------------------------------------------

function loadPersistedState(): AuthState {
  try {
    const cognitoIdToken = localStorage.getItem(STORAGE_KEY_COGNITO_ID_TOKEN);
    const hourlyToken = localStorage.getItem(STORAGE_KEY_HOURLY_TOKEN);
    const expiresAtStr = localStorage.getItem(STORAGE_KEY_COGNITO_EXPIRES_AT);

    if (cognitoIdToken && !isTokenExpired(cognitoIdToken)) {
      const claims = decodeJwtPayload<CognitoClaims>(cognitoIdToken);
      // Schedule auto-refresh for restored Cognito session
      scheduleCognitoRefresh(cognitoIdToken);
      return {
        persona: "candidate",
        hourlyToken: null,
        hourlyUser: null,
        cognitoToken: cognitoIdToken,
        candidateEmail: claims?.email ?? null,
        candidateId: claims?.sub ?? null,
      };
    }

    if (hourlyToken && !isTokenExpired(hourlyToken)) {
      const payload = decodeJwtPayload<HourlyJwtPayload>(hourlyToken);
      const userId = payload?.userId ?? payload?.sub;
      if (userId && payload?.companyId) {
        // Schedule auto-refresh for restored Hourly session
        HourlyAuthService.scheduleTokenRefresh(hourlyToken);
        return {
          persona: "manager",
          hourlyToken,
          hourlyUser: {
            userId,
            companyId: payload.companyId,
            name: payload.name ?? "",
          },
          cognitoToken: null,
          candidateEmail: null,
          candidateId: null,
        };
      }
    }
  } catch {
    // localStorage might be unavailable (e.g. private browsing restrictions)
  }

  // Clean up expired tokens
  localStorage.removeItem(STORAGE_KEY_COGNITO_ID_TOKEN);
  localStorage.removeItem(STORAGE_KEY_COGNITO_ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEY_COGNITO_REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEY_COGNITO_EXPIRES_AT);
  localStorage.removeItem(STORAGE_KEY_HOURLY_TOKEN);

  return {
    persona: "unauthenticated",
    hourlyToken: null,
    hourlyUser: null,
    cognitoToken: null,
    candidateEmail: null,
    candidateId: null,
  };
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => loadPersistedState());

  // -------------------------------------------------------------------------
  // Register token getters with the API client so api-client.ts can read
  // tokens without importing React context directly.
  // -------------------------------------------------------------------------
  const stateRef = useRef(state);
  stateRef.current = state;

  // Register token getters immediately (not in useEffect) so they're
  // available before any child component's first render/fetch.
  registerAuthTokenGetter(() => {
    const s = stateRef.current;
    if (s.persona === "candidate") return s.cognitoToken;
    if (s.persona === "manager") return s.hourlyToken;
    return null;
  });
  registerHourlyTokenGetter(() => {
    const s = stateRef.current;
    return s.persona === "manager" ? s.hourlyToken : null;
  });

  // -------------------------------------------------------------------------
  // Wire token refresh callbacks so background refreshes update React state
  // -------------------------------------------------------------------------

  useEffect(() => {
    // Hourly token refreshed in background — update state + localStorage
    HourlyAuthService.onTokenRefreshed((newTokens) => {
      try {
        localStorage.setItem(STORAGE_KEY_HOURLY_TOKEN, newTokens.accessToken);
      } catch {
        // ignore storage errors
      }
      setState((prev) => {
        if (prev.persona !== "manager") return prev;
        const payload = decodeJwtPayload<HourlyJwtPayload>(newTokens.accessToken);
        return {
          ...prev,
          hourlyToken: newTokens.accessToken,
          hourlyUser: {
            userId: payload?.userId ?? prev.hourlyUser?.userId ?? "",
            companyId: payload?.companyId ?? prev.hourlyUser?.companyId ?? "",
            name: payload?.name ?? prev.hourlyUser?.name ?? "",
          },
        };
      });
    });

    // Hourly refresh failed — auto-logout
    HourlyAuthService.onRefreshFailed(() => {
      setState((prev) => {
        if (prev.persona !== "manager") return prev;
        return {
          persona: "unauthenticated",
          hourlyToken: null,
          hourlyUser: null,
          cognitoToken: null,
          candidateEmail: null,
          candidateId: null,
        };
      });
      try {
        localStorage.removeItem(STORAGE_KEY_HOURLY_TOKEN);
      } catch {
        // ignore storage errors
      }
    });

    // Cognito token refreshed in background — update state + localStorage
    _cognitoOnRefreshed = (newTokens) => {
      setState((prev) => {
        if (prev.persona !== "candidate") return prev;
        const claims = decodeJwtPayload<CognitoClaims>(newTokens.idToken);
        return {
          ...prev,
          cognitoToken: newTokens.idToken,
          candidateEmail: claims?.email ?? prev.candidateEmail,
          candidateId: claims?.sub ?? prev.candidateId,
        };
      });
    };

    // Cognito refresh failed — redirect to login
    _cognitoOnFailed = () => {
      clearCognitoRefreshTimer();
      try {
        localStorage.removeItem(STORAGE_KEY_COGNITO_ID_TOKEN);
        localStorage.removeItem(STORAGE_KEY_COGNITO_ACCESS_TOKEN);
        localStorage.removeItem(STORAGE_KEY_COGNITO_REFRESH_TOKEN);
        localStorage.removeItem(STORAGE_KEY_COGNITO_EXPIRES_AT);
        localStorage.removeItem("ar_cognito_email");
      } catch {
        // ignore storage errors
      }
      setState((prev) => {
        if (prev.persona !== "candidate") return prev;
        return {
          persona: "unauthenticated",
          hourlyToken: null,
          hourlyUser: null,
          cognitoToken: null,
          candidateEmail: null,
          candidateId: null,
        };
      });
    };

    return () => {
      HourlyAuthService.teardown();
      clearCognitoRefreshTimer();
      _cognitoOnRefreshed = null;
      _cognitoOnFailed = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------------------------------------------------------------------------
  // Session detection on mount (already handled in loadPersistedState)
  // -------------------------------------------------------------------------

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  const loginAsManager = useCallback((tokens: HourlyTokens) => {
    const payload = decodeJwtPayload<HourlyJwtPayload>(tokens.token);
    const userId = payload?.userId ?? payload?.sub ?? "";
    const companyId = payload?.companyId ?? "";
    const name = payload?.name ?? "";

    try {
      localStorage.setItem(STORAGE_KEY_HOURLY_TOKEN, tokens.token);
    } catch {
      // ignore storage errors
    }

    // Schedule auto-refresh for new Hourly session
    HourlyAuthService.scheduleTokenRefresh(tokens.token);

    setState({
      persona: "manager",
      hourlyToken: tokens.token,
      hourlyUser: { userId, companyId, name },
      cognitoToken: null,
      candidateEmail: null,
      candidateId: null,
    });
  }, []);

  const loginAsCandidate = useCallback((tokens: CognitoTokens) => {
    const claims = decodeJwtPayload<CognitoClaims>(tokens.idToken);

    try {
      localStorage.setItem(STORAGE_KEY_COGNITO_ID_TOKEN, tokens.idToken);
      localStorage.setItem(STORAGE_KEY_COGNITO_ACCESS_TOKEN, tokens.accessToken);
      if (tokens.refreshToken) {
        localStorage.setItem(STORAGE_KEY_COGNITO_REFRESH_TOKEN, tokens.refreshToken);
      }
      if (tokens.expiresAt) {
        localStorage.setItem(STORAGE_KEY_COGNITO_EXPIRES_AT, String(tokens.expiresAt));
      }
      // Persist email for Cognito refresh (needed to reconstruct CognitoUser)
      if (claims?.email) {
        localStorage.setItem("ar_cognito_email", claims.email);
      }
    } catch {
      // ignore storage errors
    }

    // Schedule auto-refresh for new Cognito session
    scheduleCognitoRefresh(tokens.idToken);

    setState({
      persona: "candidate",
      hourlyToken: null,
      hourlyUser: null,
      cognitoToken: tokens.idToken,
      candidateEmail: claims?.email ?? null,
      candidateId: claims?.sub ?? null,
    });
  }, []);

  const logout = useCallback(() => {
    // Cancel all pending refresh timers
    HourlyAuthService.teardown();
    clearCognitoRefreshTimer();

    try {
      localStorage.removeItem(STORAGE_KEY_COGNITO_ID_TOKEN);
      localStorage.removeItem(STORAGE_KEY_COGNITO_ACCESS_TOKEN);
      localStorage.removeItem(STORAGE_KEY_COGNITO_REFRESH_TOKEN);
      localStorage.removeItem(STORAGE_KEY_COGNITO_EXPIRES_AT);
      localStorage.removeItem(STORAGE_KEY_HOURLY_TOKEN);
      localStorage.removeItem("ar_cognito_email");
    } catch {
      // ignore storage errors
    }

    setState({
      persona: "unauthenticated",
      hourlyToken: null,
      hourlyUser: null,
      cognitoToken: null,
      candidateEmail: null,
      candidateId: null,
    });
  }, []);

  const getHiringApiToken = useCallback((): string | null => {
    if (state.persona === "candidate") return state.cognitoToken;
    if (state.persona === "manager") return state.hourlyToken;
    return null;
  }, [state]);

  const getHourlyApiToken = useCallback((): string | null => {
    if (state.persona === "manager") return state.hourlyToken;
    return null;
  }, [state]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      loginAsManager,
      loginAsCandidate,
      logout,
      getHiringApiToken,
      getHourlyApiToken,
    }),
    [state, loginAsManager, loginAsCandidate, logout, getHiringApiToken, getHourlyApiToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
